// =============================================
// useTransferWizard — state machine kreatora "Przenies domeny" (4 kroki).
// Trzymany w CloudflarePage (przezywa zmiane zakladki). Run zyje w
// transferRunStore (przezywa odmontowanie strony).
//
//   krok 1: wybor domen + sprawdzenie NS (sekwencyjnie nsGet) + "+ domena spoza listy"
//   krok 2: konfiguracja 3 akcji
//   krok 3: plan (buildTransferPlan — czysta funkcja, memo)
//   krok 4: run (runTransfer) + Ponow per wiersz
// =============================================
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CloudflareAccount } from "@/hooks/useCloudflareAccounts";
import type { AllZonesState } from "@/hooks/useAllZones";
import type { TransferDomainsState } from "@/hooks/useTransferDomains";
import { nsGet, normalizeDomain } from "@/services/aftermarketService";
import { addZone, pointDomainToIp } from "@/services/cloudflareService";
import { nsSet } from "@/services/aftermarketService";
import { insertDnsChangeLog } from "@/services/cloudflare/dnsChangeLogService";
import { buildTransferPlan, suggestLeastLoadedAccount } from "@/services/cloudflare/transferPlanBuilder";
import { runTransfer, type TransferRunnerDeps } from "@/services/cloudflare/transferRunner";
import type { NsCache, TransferConfig, TransferDomain, TransferPlan } from "@/services/cloudflare/transferTypes";
import { transferRunStore, useTransferRunStore, TRANSFER_LEAVE_MSG } from "@/lib/transferRunStore";

export type WizardStep = 1 | 2 | 3 | 4;

const DEFAULT_CONFIG: TransferConfig = {
  createZone: { enabled: true, cfAccountId: null },
  setNs: { enabled: true, mode: "from_zone", customNs: ["", ""] },
  aRecord: { enabled: true, proxied: true, ipOverrides: {} },
};

interface Args {
  accounts: CloudflareAccount[];
  allZones: AllZonesState;
  transferDomains: TransferDomainsState;
}

export function useTransferWizard({ accounts, allZones, transferDomains }: Args) {
  const run = useTransferRunStore();
  const [step, setStep] = useState<WizardStep>(run.status === "idle" ? 1 : 4);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [manualDomains, setManualDomains] = useState<TransferDomain[]>([]);
  const [nsCache, setNsCache] = useState<NsCache>({});
  const [nsCheck, setNsCheck] = useState<{ running: boolean; done: number; total: number }>({ running: false, done: 0, total: 0 });
  const [config, setConfigState] = useState<TransferConfig>(DEFAULT_CONFIG);
  const [autoSuggested, setAutoSuggested] = useState(false);
  const nsAbort = useRef<AbortController | null>(null);

  // --- domeny kroku 1: z panelu (workspace) + reczne (tylko biezacy workspace) ---
  const { effectiveWorkspace } = transferDomains;
  const candidates: TransferDomain[] = useMemo(() => {
    const keys = new Set(transferDomains.domains.map((d) => d.key));
    const manual = manualDomains.filter((m) => m.workspace === effectiveWorkspace && !keys.has(m.key));
    return [...transferDomains.domains, ...manual].sort((a, b) => a.key.localeCompare(b.key));
  }, [transferDomains.domains, manualDomains, effectiveWorkspace]);
  const candidateByKey = useMemo(() => new Map(candidates.map((d) => [d.key, d] as const)), [candidates]);

  // zmiana workspace = inna lista → czyscimy wybor (nie mieszamy kont)
  const prevWs = useRef(effectiveWorkspace);
  useEffect(() => {
    if (prevWs.current !== effectiveWorkspace) {
      prevWs.current = effectiveWorkspace;
      setSelectedKeys(new Set());
    }
  }, [effectiveWorkspace]);

  const selected: TransferDomain[] = useMemo(
    () => [...selectedKeys].map((k) => candidateByKey.get(k)).filter((d): d is TransferDomain => !!d),
    [selectedKeys, candidateByKey],
  );

  // --- akcje wyboru ---
  const toggle = useCallback((key: string) => setSelectedKeys((prev) => {
    const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next;
  }), []);
  const selectMany = useCallback((keys: string[], on: boolean) => setSelectedKeys((prev) => {
    const next = new Set(prev); for (const k of keys) { if (on) next.add(k); else next.delete(k); } return next;
  }), []);
  const clearSelection = useCallback(() => setSelectedKeys(new Set()), []);

  /** "+ Dodaj domene spoza listy" — zwraca blad albo null. */
  const addManualDomain = useCallback((raw: string): string | null => {
    const key = normalizeDomain(raw);
    if (!key || !key.includes(".")) return "Podaj poprawną domenę (np. przyklad.pl).";
    if (candidateByKey.has(key)) return "Ta domena już jest na liście.";
    setManualDomains((prev) => [...prev, { key, websiteId: null, workspace: effectiveWorkspace, expiry: null, expirySource: null, hostingIp: null, manual: true }]);
    setSelectedKeys((prev) => new Set(prev).add(key));
    return null;
  }, [candidateByKey, effectiveWorkspace]);

  // --- sprawdzenie NS (sekwencyjnie, przerywalne) ---
  const checkNs = useCallback(async (keys: string[], force = false) => {
    const todo = keys.filter((k) => force || nsCache[k]?.status !== "ok");
    if (!todo.length) return;
    nsAbort.current?.abort();
    const ctrl = new AbortController();
    nsAbort.current = ctrl;
    setNsCheck({ running: true, done: 0, total: todo.length });
    let done = 0;
    for (const key of todo) {
      if (ctrl.signal.aborted) break;
      const d = candidateByKey.get(key);
      const ws = d?.workspace ?? effectiveWorkspace;
      try {
        const ns = await nsGet(key, ws);
        setNsCache((prev) => ({ ...prev, [key]: { status: "ok", ns } }));
      } catch (e) {
        setNsCache((prev) => ({ ...prev, [key]: { status: "error", error: e instanceof Error ? e.message : String(e) } }));
      }
      done++;
      setNsCheck({ running: true, done, total: todo.length });
    }
    setNsCheck({ running: false, done, total: todo.length });
  }, [nsCache, candidateByKey, effectiveWorkspace]);
  const cancelNsCheck = useCallback(() => nsAbort.current?.abort(), []);

  // --- konfiguracja ---
  const setConfig = useCallback((patch: Partial<TransferConfig> | ((c: TransferConfig) => TransferConfig)) => {
    setConfigState((c) => (typeof patch === "function" ? patch(c) : { ...c, ...patch }));
  }, []);

  // auto-propozycja najmniej obciazonego konta przy pierwszym wejsciu w krok 2
  useEffect(() => {
    if (step !== 2 || autoSuggested || config.createZone.cfAccountId) return;
    const s = suggestLeastLoadedAccount(accounts, allZones.zoneCountByAccount);
    if (s) setConfigState((c) => ({ ...c, createZone: { ...c.createZone, cfAccountId: s } }));
    setAutoSuggested(true);
  }, [step, autoSuggested, config.createZone.cfAccountId, accounts, allZones.zoneCountByAccount]);

  // --- plan (memo; w kroku 4 uzywany plan zamrozony w store) ---
  const plan: TransferPlan = useMemo(
    () => buildTransferPlan(selected, config, { zones: allZones.zones, accounts, nsCache }),
    [selected, config, allZones.zones, accounts, nsCache],
  );

  // --- run ---
  const deps: TransferRunnerDeps = useMemo(() => ({
    addZone, nsSet, pointDomainToIp, log: insertDnsChangeLog,
    getToken: (id) => accounts.find((a) => a.id === id)?.api_token,
    getAccountId: (id) => accounts.find((a) => a.id === id)?.account_id?.trim() || undefined,
  }), [accounts]);

  const startRun = useCallback(async () => {
    if (run.status === "running") return;
    transferRunStore.start(plan, false);
    setStep(4);
    const control = { isStopRequested: transferRunStore.isStopRequested };
    try {
      const summary = await runTransfer(plan, deps, control, (row) => {
        transferRunStore.setRow(row);
        if (row.zone_create.status === "ok" && row.zone_create.zone) {
          const pr = plan.rows.find((r) => r.domain.key === row.key);
          const accId = pr?.tokenCfAccountId;
          if (accId) allZones.appendZone(row.zone_create.zone, accId);
        }
      });
      transferRunStore.finish(summary);
    } catch (e) {
      transferRunStore.finish({ total: plan.rows.length, ok: 0, failed: 0, skipped: 0, stoppedReason: "user" });
      throw e;
    }
  }, [run.status, plan, deps, allZones]);

  /** Ponow jeden wiersz (dialog NS obsluguje Step4 przed wywolaniem). */
  const retryRow = useCallback(async (key: string) => {
    const frozen = transferRunStore.get().plan;
    if (!frozen || run.status === "running") return;
    transferRunStore.start(frozen, true);
    const control = { isStopRequested: transferRunStore.isStopRequested };
    const summary = await runTransfer(frozen, deps, control, (row) => transferRunStore.setRow(row), {
      onlyKeys: [key],
      alreadyDone: transferRunStore.get().rows,
    });
    const prev = run.summary;
    transferRunStore.finish(prev ? { ...prev, ok: prev.ok + summary.ok, failed: Math.max(0, prev.failed - (summary.ok ? 1 : 0)), stoppedReason: null } : summary);
  }, [run.status, run.summary, deps]);

  const stopRun = useCallback(() => transferRunStore.requestStop(), []);

  const reset = useCallback(() => {
    transferRunStore.reset();
    setStep(1);
    setSelectedKeys(new Set());
    setConfigState(DEFAULT_CONFIG);
    setAutoSuggested(false);
  }, []);

  // beforeunload na zamkniecie karty w trakcie runu (nawigacje SPA pilnuje sidebar)
  useEffect(() => {
    if (run.status !== "running") return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = TRANSFER_LEAVE_MSG; return e.returnValue; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [run.status]);

  // --- bramki "Dalej" ---
  const zonesReady = allZones.loadedAt !== null && !allZones.loading && allZones.zones.length + allZones.failures.length >= 0 && allZones.failures.length < Math.max(1, accounts.length);
  const canNext1 = selectedKeys.size > 0 && zonesReady;
  const canNext2 =
    (config.createZone.enabled || config.setNs.enabled || config.aRecord.enabled) &&
    (!config.createZone.enabled || !!config.createZone.cfAccountId);
  const canNext3 = plan.runnableCount > 0 && run.status !== "running";

  return {
    step, setStep,
    candidates, selected, selectedKeys, toggle, selectMany, clearSelection, addManualDomain,
    nsCache, nsCheck, checkNs, cancelNsCheck,
    config, setConfig,
    plan,
    run, startRun, retryRow, stopRun, reset,
    zonesReady, canNext1, canNext2, canNext3,
  };
}

export type TransferWizardState = ReturnType<typeof useTransferWizard>;
